import { useState, useEffect, useMemo } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Plus, X, Eye, Check, Loader2, Image, Upload, AlertTriangle, Star, Sparkles, ExternalLink } from 'lucide-react'
import { productAI, products, ProductCreateRequest, OpenAIUsage, supplierCatalogsApi, itsysImagesApi, ItsysImageInfo, ItsysProductImagesResponse } from '@/lib/api'

// Helper to extract error message from FastAPI/Axios errors
function getErrorMessage(error: any): string {
  if (!error) return 'Error desconocido'

  // Check for FastAPI validation error format (array of objects)
  const detail = error.response?.data?.detail
  if (detail) {
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      // Extract 'msg' from each validation error
      return detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ')
    }
    if (typeof detail === 'object' && detail.msg) {
      return detail.msg
    }
  }

  // Fallback to error message
  return error.message || 'Error desconocido'
}
import { generateClientId } from '@/lib/utils'
import { useWebSocket, ProgressMessage } from '@/hooks/useWebSocket'
import ProductPreview from '@/components/ProductPreview'
import SearchableSelect from '@/components/SearchableSelect'
import { OpenAICostDisplay } from '@/components/OpenAICostDisplay'
import AiReadinessBadge from '@/components/AiReadinessBadge'
import { useToast } from '@/components/ui/Toast'
import { useFormValidation, ValidationSchema } from '@/hooks/useFormValidation'

// Modal de confirmación
interface ConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  productData: {
    title?: string
    reference?: string
    costPrice?: string
    categoryId?: string
    categoryName?: string
    createInFactusol: boolean
    factusolFamily?: string
    factusolSupplier?: string
    imageCount: number
  }
  isLoading: boolean
}

function ConfirmModal({ isOpen, onClose, onConfirm, productData, isLoading }: ConfirmModalProps) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-primary-50 dark:bg-primary-900/30 px-6 py-4 border-b border-primary-100 dark:border-primary-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-800 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Confirmar Creación
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Revisa los datos antes de crear
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          <div className="space-y-3">
            {productData.title && (
              <div className="flex justify-between items-start">
                <span className="text-sm text-slate-500 dark:text-slate-400">Nombre:</span>
                <span className="text-sm font-medium text-slate-900 dark:text-white text-right max-w-[60%] truncate">
                  {productData.title}
                </span>
              </div>
            )}

            {productData.reference && (
              <div className="flex justify-between">
                <span className="text-sm text-slate-500 dark:text-slate-400">Referencia:</span>
                <span className="text-sm font-medium text-slate-900 dark:text-white font-mono">
                  {productData.reference}
                </span>
              </div>
            )}

            {productData.costPrice && (
              <div className="flex justify-between">
                <span className="text-sm text-slate-500 dark:text-slate-400">Precio Coste:</span>
                <span className="text-sm font-medium text-slate-900 dark:text-white">
                  {productData.costPrice} €
                </span>
              </div>
            )}

            {productData.categoryId && (
              <div className="flex justify-between">
                <span className="text-sm text-slate-500 dark:text-slate-400">Categoría:</span>
                <span className="text-sm font-medium text-slate-900 dark:text-white text-right max-w-[60%]">
                  {productData.categoryName || `ID: ${productData.categoryId}`}
                </span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-sm text-slate-500 dark:text-slate-400">Imágenes:</span>
              <span className="text-sm font-medium text-slate-900 dark:text-white">
                {productData.imageCount}
              </span>
            </div>

            {/* Factusol section */}
            <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500 dark:text-slate-400">Crear en Factusol:</span>
                <span className={`text-sm font-medium ${productData.createInFactusol ? 'text-success-600 dark:text-success-400' : 'text-slate-400'}`}>
                  {productData.createInFactusol ? 'Sí' : 'No'}
                </span>
              </div>

              {productData.createInFactusol && (
                <>
                  {productData.factusolFamily && (
                    <div className="flex justify-between mt-2">
                      <span className="text-sm text-slate-500 dark:text-slate-400 pl-4">Familia:</span>
                      <span className="text-sm text-slate-900 dark:text-white">
                        {productData.factusolFamily}
                      </span>
                    </div>
                  )}
                  {productData.factusolSupplier && (
                    <div className="flex justify-between mt-1">
                      <span className="text-sm text-slate-500 dark:text-slate-400 pl-4">Proveedor:</span>
                      <span className="text-sm text-slate-900 dark:text-white">
                        {productData.factusolSupplier}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
            <p className="text-xs text-yellow-800 dark:text-yellow-200">
              Esta acción creará el producto en PrestaShop y no se puede deshacer fácilmente.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 flex gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="btn btn-secondary flex-1"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="btn btn-primary flex-1"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creando...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Crear Producto
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

interface ProgressState {
  step: number
  total: number
  message: string
  percentage: number
  elapsed_seconds?: number
}

// Step descriptions for UI display
const PROGRESS_STEPS = [
  { step: 1, label: 'Analizando URLs' },
  { step: 2, label: 'Generando HTML' },
  { step: 3, label: 'Calculando precios' },
  { step: 4, label: 'Creando en BD' },
  { step: 5, label: 'Traduciendo' },
  { step: 6, label: 'Subiendo imagenes' },
  { step: 7, label: 'Completado' },
]

// Image validation constants
const MAX_IMAGE_SIZE_MB = 5
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

export default function ProductCreatorPage() {
  const toast = useToast()
  const [inputMode, setInputMode] = useState<'url' | 'text'>('url') // Modo de entrada
  const [urls, setUrls] = useState<string[]>([''])
  const [textContent, setTextContent] = useState('')
  const [additionalInfo, setAdditionalInfo] = useState('') // Info adicional para complementar URLs
  const [categoryId, setCategoryId] = useState<string>('')
  const [reference, setReference] = useState('')
  const [referenceLoading, setReferenceLoading] = useState(false) // Loading para auto-completar referencia
  const [referenceInfo, setReferenceInfo] = useState<{ highest: string | null; count: number } | null>(null) // Info sobre referencias existentes
  const [supplierReference, setSupplierReference] = useState('') // Referencia del proveedor (para búsqueda ITSys)
  const [costPrice, setCostPrice] = useState('')
  const [warrantyDuration, setWarrantyDuration] = useState('3 años')
  // Modelo fijo: Gemini 2.5 Pro (sin selector; OpenAI sin saldo y Gemini 2.0 retirado)
  const aiModel = 'gemini-2.5-pro'
  const [imageFiles, setImageFiles] = useState<File[]>([])
  const [imageErrors, setImageErrors] = useState<{ [key: number]: string }>({}) // Errores de validación de imagen
  const [selectedScrapedImages, setSelectedScrapedImages] = useState<string[]>([]) // URLs de imágenes del scraping seleccionadas
  const [previewOnly, setPreviewOnly] = useState(true)
  const [addToQueue, setAddToQueue] = useState(false)  // Añadir a cola en lugar de procesar ahora
  const [autoApprove, setAutoApprove] = useState(true)  // Aprobar y publicar automáticamente

  const [progress, setProgress] = useState<ProgressState | null>(null)
  const [result, setResult] = useState<any>(null)
  const [clientId] = useState(generateClientId())
  const [lastRequestData, setLastRequestData] = useState<ProductCreateRequest | null>(null) // Guardar última petición
  const [wsEnabled, setWsEnabled] = useState(false) // Control WebSocket connection
  const [showConfirmModal, setShowConfirmModal] = useState(false) // Modal de confirmación
  const [pendingRequestData, setPendingRequestData] = useState<ProductCreateRequest | null>(null) // Datos pendientes de confirmar
  const [showDuplicateModal, setShowDuplicateModal] = useState(false) // Modal de producto duplicado
  const [duplicateInfo, setDuplicateInfo] = useState<{ id_product: number, name: string, reference: string } | null>(null)

  // Factusol State
  const [createInFactusol, setCreateInFactusol] = useState(false)
  const [selectedFamily, setSelectedFamily] = useState('')
  const [selectedSupplier, setSelectedSupplier] = useState('')
  const [supplierRef, setSupplierRef] = useState('')

  // Supplier Images State (para procesar imágenes con IA desde catálogo de proveedor)
  const [supplierIdParam, setSupplierIdParam] = useState<number | null>(null)
  const [productIdParam, setProductIdParam] = useState<number | null>(null)
  const [supplierImages, setSupplierImages] = useState<Array<{
    index: number
    base64: string
    filename: string
    has_edited_version: boolean
    processed_base64?: string  // Imagen procesada (si se editó)
  }>>([])
  const [selectedImageIndices, setSelectedImageIndices] = useState<number[]>([])
  const [imageInstructions, setImageInstructions] = useState('')
  const [processingImages, setProcessingImages] = useState(false)
  const [currentProcessingIndex, setCurrentProcessingIndex] = useState<number | null>(null)
  const [totalImageCost, setTotalImageCost] = useState({ usd: 0, eur: 0 })
  const [loadingSupplierImages, setLoadingSupplierImages] = useState(false)

  // ITSys Images State (imágenes locales del proveedor ITSys)
  const [itsysImages, setItsysImages] = useState<ItsysImageInfo[]>([])
  const [selectedItsysImages, setSelectedItsysImages] = useState<string[]>([])
  const [itsysCoverImage, setItsysCoverImage] = useState<string | null>(null) // Imagen principal/cover
  const [loadingItsysImages, setLoadingItsysImages] = useState(false)
  const [itsysCategory, setItsysCategory] = useState<string | null>(null)
  const [hoveredItsysImage, setHoveredItsysImage] = useState<ItsysImageInfo | null>(null)
  // Limpieza de marca de agua con PixelBin cuando no hay fotos limpias de la referencia
  const [cleaningItsys, setCleaningItsys] = useState(false)
  const [itsysCleanMsg, setItsysCleanMsg] = useState<{ type: 'ok' | 'error', text: string } | null>(null)
  const [autoCleanedRefs] = useState(() => new Set<string>())

  // Fetch Factusol Data with React Query cache (5 min stale time)
  const { data: familiesData } = useQuery({
    queryKey: ['factusol-families'],
    queryFn: () => productAI.getFactusolFamilies(),
    staleTime: 5 * 60 * 1000, // 5 minutos cache
    gcTime: 10 * 60 * 1000, // 10 minutos en garbage collection
  })

  const { data: suppliersData } = useQuery({
    queryKey: ['factusol-suppliers'],
    queryFn: () => productAI.getFactusolSuppliers(),
    staleTime: 5 * 60 * 1000, // 5 minutos cache
    gcTime: 10 * 60 * 1000, // 10 minutos en garbage collection
  })

  // Fetch PrestaShop categories
  const { data: categoriesData } = useQuery({
    queryKey: ['prestashop-categories'],
    queryFn: () => products.listCategoriesFromDB(),
    staleTime: 5 * 60 * 1000, // 5 minutos cache
    gcTime: 10 * 60 * 1000, // 10 minutos en garbage collection
  })

  const factusolFamilies = familiesData?.families || []
  const factusolSuppliers = suppliersData?.suppliers || []
  const prestashopCategories = categoriesData?.categories || []

  // Validation schema for form fields
  type FormValues = {
    url: string
    textContent: string
    costPrice: string
    reference: string
    factusolFamily: string
    factusolSupplier: string
  }

  const validationSchema = useMemo<ValidationSchema<FormValues>>(() => ({
    url: {
      custom: [{
        validate: (value: string) => {
          if (inputMode !== 'url') return true
          const validUrls = urls.filter(u => u.trim() !== '')
          return validUrls.length > 0
        },
        message: 'Debe proporcionar al menos una URL'
      }]
    },
    textContent: {
      custom: [{
        validate: (value: string) => {
          if (inputMode !== 'text') return true
          return value.trim().length > 0
        },
        message: 'Debe proporcionar contenido de texto'
      }]
    },
    costPrice: {
      min: { value: 0, message: 'El precio no puede ser negativo' },
      custom: [{
        validate: (value: string) => {
          if (!value) return true
          const num = parseFloat(value)
          return !isNaN(num)
        },
        message: 'Ingrese un número válido'
      }]
    },
    reference: {
      maxLength: { value: 64, message: 'Máximo 64 caracteres' }
    },
    factusolFamily: {
      custom: [{
        validate: (value: string) => {
          if (!createInFactusol) return true
          const strValue = String(value || '')
          return strValue.trim().length > 0
        },
        message: 'Seleccione una familia para Factusol'
      }]
    },
    factusolSupplier: {
      custom: [{
        validate: (value: string) => {
          if (!createInFactusol) return true
          const strValue = String(value || '')
          return strValue.trim().length > 0
        },
        message: 'Seleccione un proveedor para Factusol'
      }]
    }
  }), [inputMode, urls, createInFactusol])

  const formValues: FormValues = {
    url: urls[0] || '',
    textContent,
    costPrice,
    reference,
    factusolFamily: selectedFamily,
    factusolSupplier: selectedSupplier
  }

  const { visibleErrors, touchAll, isValid, getFieldProps, resetTouched } = useFormValidation(formValues, validationSchema)

  // URL validation helper for individual URL inputs
  const isValidUrl = (url: string): boolean => {
    if (!url.trim()) return true // Empty is ok
    return /^https?:\/\/.+/i.test(url)
  }

  // Track URL-specific errors
  const [urlErrors, setUrlErrors] = useState<{ [key: number]: string }>({})

  const validateUrl = (index: number, value: string) => {
    const newErrors = { ...urlErrors }
    if (value.trim() && !isValidUrl(value)) {
      newErrors[index] = 'URL debe comenzar con http:// o https://'
    } else {
      delete newErrors[index]
    }
    setUrlErrors(newErrors)
  }

  // Leer parámetros de URL para pre-rellenar el formulario (desde catálogo de proveedor)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  useEffect(() => {
    const name = searchParams.get('name')
    const ref = searchParams.get('reference')
    const cost = searchParams.get('cost_price')
    const ean = searchParams.get('ean')
    const supplierRefParam = searchParams.get('supplier_ref')
    const supplierCode = searchParams.get('supplier_code')
    const supplierId = searchParams.get('supplier_id')
    const productId = searchParams.get('product_id')

    // Si hay supplier_id y product_id, los datos completos se cargarán en otro useEffect
    // No pre-rellenar textContent aquí para permitir que el fetch cargue descripción + specs
    const willFetchFullData = supplierId && productId

    // Si hay nombre, cambiar a modo texto y pre-rellenar (solo si no hay supplier_id/product_id)
    if (name) {
      setInputMode('text')
      if (!willFetchFullData) {
        // Solo pre-rellenar con datos básicos si no vamos a cargar datos completos
        let content = name
        if (ean) {
          content += `\n\nEAN: ${ean}`
        }
        if (ref) {
          content += `\nReferencia proveedor: ${ref}`
        }
        setTextContent(content)
      }
    }

    // Pre-rellenar precio de coste
    if (cost) {
      setCostPrice(cost)
    }

    // Pre-rellenar referencia del proveedor (para PrestaShop e ITSys)
    // Usar supplier_ref si existe, o reference como fallback
    const supplierRefValue = supplierRefParam || ref
    if (supplierRefValue) {
      setSupplierReference(supplierRefValue)
    }

    // Pre-rellenar referencia del proveedor para Factusol
    if (supplierRefParam) {
      setSupplierRef(supplierRefParam)
    }

    // Pre-seleccionar proveedor en Factusol si viene el código (CODPRO)
    if (supplierCode) {
      setSelectedSupplier(supplierCode)
      setCreateInFactusol(true)
    }

    // Leer supplier_id y product_id para cargar datos completos del producto
    console.log('[ProductCreator] URL params:', { supplierId, productId })
    if (supplierId && productId) {
      console.log('[ProductCreator] Setting supplier/product IDs:', parseInt(supplierId), parseInt(productId))
      setSupplierIdParam(parseInt(supplierId))
      setProductIdParam(parseInt(productId))
    }
  }, [searchParams])

  // Cargar datos del producto y sus imágenes cuando hay supplier_id y product_id
  useEffect(() => {
    const loadSupplierProductData = async () => {
      console.log('[ProductCreator] Checking supplier product data:', { supplierIdParam, productIdParam })
      if (!supplierIdParam || !productIdParam) {
        console.log('[ProductCreator] No supplier/product IDs, skipping load')
        return
      }

      console.log('[ProductCreator] Loading supplier product data...')
      setLoadingSupplierImages(true)
      try {
        // Cargar datos del producto (description y extra_data con specifications)
        const product = await supplierCatalogsApi.getProduct(supplierIdParam, productIdParam)
        console.log('[ProductCreator] Product data loaded:', product.name)

        // Función para eliminar la referencia del proveedor del texto (100% de los casos)
        const removeSupplierRef = (text: string, ref: string): string => {
          if (!text || !ref) return text || ''
          try {
            // Escapar caracteres especiales de regex
            const escapedRef = ref.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            return text
              // Eliminar patrones como "( SKU.REF )" o "(SKU.REF)" o "SKU: REF"
              .replace(new RegExp(`\\s*\\(?\\s*SKU[.:\\s]*${escapedRef}\\s*\\)?`, 'gi'), '')
              // Eliminar "Referencia: REF" o "Ref: REF" o "Ref. REF"
              .replace(new RegExp(`\\s*Ref(?:erencia)?[.:\\s]*${escapedRef}`, 'gi'), '')
              // Eliminar la referencia entre paréntesis: "(REF)"
              .replace(new RegExp(`\\s*\\(\\s*${escapedRef}\\s*\\)`, 'gi'), '')
              // Eliminar la referencia entre corchetes: "[REF]"
              .replace(new RegExp(`\\s*\\[\\s*${escapedRef}\\s*\\]`, 'gi'), '')
              // Eliminar la referencia precedida por guión o dos puntos: "- REF" o ": REF"
              .replace(new RegExp(`\\s*[-:]\\s*${escapedRef}`, 'gi'), '')
              // Eliminar la referencia en cualquier parte del texto (case insensitive)
              .replace(new RegExp(escapedRef, 'gi'), '')
              // Limpiar puntuación huérfana (preservando saltos de línea)
              .replace(/[ \t]*[,;][ \t]*[,;][ \t]*/g, ', ')  // Doble coma/punto y coma
              .replace(/[ \t]+/g, ' ')  // Normalizar solo espacios horizontales (no newlines)
              .replace(/^\s*[,;.\-:]\s*/gm, '')  // Puntuación al inicio de cada línea
              .replace(/\s*[,;.\-:]\s*$/gm, '')  // Puntuación al final de cada línea
              .replace(/\n{3,}/g, '\n\n')  // Máximo 2 saltos de línea seguidos
              .trim()
          } catch (e) {
            console.error('[ProductCreator] Error in removeSupplierRef:', e)
            return text
          }
        }

        const supplierRef = product.supplier_reference

        // Construir texto con descripción y especificaciones
        let fullText = ''

        // Añadir descripción (limpiando HTML y referencia del proveedor)
        if (product.description) {
          let cleanDescription = product.description
            .replace(/<[^>]*>/g, ' ')  // Quitar tags HTML
            .replace(/&nbsp;/g, ' ')    // Reemplazar espacios HTML
            .replace(/\s+/g, ' ')       // Normalizar espacios
            .trim()
          // Eliminar referencia del proveedor
          cleanDescription = removeSupplierRef(cleanDescription, supplierRef)
          if (cleanDescription) {
            fullText += cleanDescription
          }
        }

        // Añadir especificaciones desde extra_data
        if (product.extra_data) {
          try {
            const extraData = JSON.parse(product.extra_data)
            if (extraData.specifications && typeof extraData.specifications === 'object') {
              const specs = Object.entries(extraData.specifications)
                .map(([key, value]) => `${key}: ${value}`)
                .join('\n')
              if (specs) {
                fullText += (fullText ? '\n\n' : '') + 'Especificaciones:\n' + specs
              }
            }
          } catch (e) {
            console.error('[ProductCreator] Error parsing extra_data:', e)
          }
        }

        // Pre-rellenar el campo de texto con los datos completos del producto
        // Limpiar también el nombre de la referencia del proveedor
        const cleanName = removeSupplierRef(product.name, supplierRef)
        if (fullText) {
          // Limpiar TODO el texto final de la referencia del proveedor
          const finalText = removeSupplierRef(cleanName + '\n\n' + fullText, supplierRef)
          setTextContent(finalText)
          console.log('[ProductCreator] Text content pre-filled with description and specs')
        } else {
          setTextContent(cleanName)
          console.log('[ProductCreator] Text content set to product name only')
        }

        // Cargar imágenes del proveedor
        const response = await supplierCatalogsApi.getProductImages(supplierIdParam, productIdParam)
        console.log('[ProductCreator] Images loaded:', response.images.length)
        setSupplierImages(response.images.map(img => ({
          ...img,
          processed_base64: undefined
        })))
        // Seleccionar todas las imágenes por defecto
        setSelectedImageIndices(response.images.map(img => img.index))
      } catch (error) {
        console.error('[ProductCreator] Error cargando datos del proveedor:', error)
      } finally {
        setLoadingSupplierImages(false)
      }
    }

    loadSupplierProductData()
  }, [supplierIdParam, productIdParam])

  // Proveedor ITSYS: código del catálogo ('ITSYS') o de Factusol ('23')
  const isItsysSupplier = ['ITSYS', '23'].includes(String(selectedSupplier ?? '').toUpperCase())

  // Mostrar en la galería las fotos ITSys encontradas (una por índice, la de mayor resolución)
  const applyItsysImages = (result: ItsysProductImagesResponse): boolean => {
    if (!result.found || result.images.length === 0) {
      setItsysImages([])
      setSelectedItsysImages([])
      setItsysCoverImage(null)
      setItsysCategory(null)
      return false
    }
    const bestImages: ItsysImageInfo[] = []
    const seenIndices = new Set<number | null>()
    const sortedImages = [...result.images].sort((a, b) => (b.width * b.height) - (a.width * a.height))
    for (const img of sortedImages) {
      if (!seenIndices.has(img.index)) {
        seenIndices.add(img.index)
        bestImages.push(img)
      }
    }
    // Ordenar por índice (null = principal primero)
    bestImages.sort((a, b) => {
      if (a.index === null && b.index !== null) return -1
      if (a.index !== null && b.index === null) return 1
      return (a.index || 0) - (b.index || 0)
    })
    setItsysImages(bestImages)
    setItsysCategory(result.category)
    // Seleccionar todas por defecto y la primera como portada
    setSelectedItsysImages(bestImages.map(img => img.url))
    setItsysCoverImage(bestImages[0].url)
    return true
  }

  // Descargar de ITSYS, quitar la marca de agua con PixelBin y cargar las fotos limpias
  const cleanItsysImages = async (ref: string, force = false) => {
    setCleaningItsys(true)
    setItsysCleanMsg(null)
    try {
      const result = await itsysImagesApi.clean(ref, force)
      const shown = applyItsysImages(result)
      if (shown) {
        const detalle = result.ya_existian
          ? 'ya estaban limpias'
          : `${result.unique_images_count} foto(s), ${result.pixelbin} limpiada(s) con PixelBin`
        setItsysCleanMsg(result.error
          ? { type: 'error', text: `Fotos de ITSYS: ${detalle}. Aviso: ${result.error}` }
          : { type: 'ok', text: `Fotos de ITSYS sin marca de agua: ${detalle}.` })
      } else {
        setItsysCleanMsg({ type: 'error', text: result.error || 'No se pudieron obtener fotos limpias de ITSYS' })
      }
    } catch (error: any) {
      console.error('[ProductCreator] Error limpiando imágenes ITSys:', error)
      setItsysCleanMsg({ type: 'error', text: error?.response?.data?.detail || error?.message || 'Error limpiando las fotos de ITSYS' })
    } finally {
      setCleaningItsys(false)
    }
  }

  // Buscar imágenes ITSys cuando cambia la referencia del proveedor (con debounce)
  useEffect(() => {
    const searchItsysImages = async () => {
      // Usar el campo "Referencia Proveedor" para buscar imágenes ITSys
      const refToSearch = supplierReference
      setItsysCleanMsg(null)
      if (!refToSearch || refToSearch.length < 3) {
        applyItsysImages({ found: false, images: [] } as unknown as ItsysProductImagesResponse)
        return
      }

      setLoadingItsysImages(true)
      let found = false
      try {
        const result = await itsysImagesApi.search(refToSearch)
        // La búsqueda admite coincidencias parciales: en ITSYS solo valen las fotos de esta
        // referencia (ignorando sufijos entre paréntesis como "(2.2Ah)"), no las de otra que la contenga
        const norm = (r: string) => r.replace(/\([^)]*\)/g, '').replace(/[^a-zA-Z0-9-]/g, '').toLowerCase()
        const exact = result.found && norm(result.folder_name || '') === norm(refToSearch)
        found = applyItsysImages(exact || !isItsysSupplier ? result : { ...result, found: false })
      } catch (error) {
        console.error('[ProductCreator] Error buscando imágenes ITSys:', error)
        applyItsysImages({ found: false, images: [] } as unknown as ItsysProductImagesResponse)
      } finally {
        setLoadingItsysImages(false)
      }

      // Alta desde el catálogo de ITSYS sin fotos limpias: limpiarlas automáticamente (una vez por ref)
      if (!found && isItsysSupplier && productIdParam && !autoCleanedRefs.has(refToSearch)) {
        autoCleanedRefs.add(refToSearch)
        await cleanItsysImages(refToSearch)
      }
    }

    // Debounce de 500ms para no buscar en cada tecla
    const timer = setTimeout(searchItsysImages, 500)
    return () => clearTimeout(timer)
  }, [supplierReference, isItsysSupplier, productIdParam])

  // Función para buscar siguiente referencia secuencial (acción manual)
  const fetchNextSequentialReference = async () => {
    // Necesita un prefijo (puede terminar en "-" o no)
    if (!reference || reference.length < 1) {
      return
    }

    // Añadir "-" si no lo tiene
    const prefix = reference.endsWith('-') ? reference : reference + '-'

    console.log('[ProductCreator] Fetching next reference for prefix:', prefix)
    setReferenceLoading(true)
    setReferenceInfo(null)

    try {
      const result = await productAI.getNextSequentialReference(prefix)
      console.log('[ProductCreator] Next reference result:', result)

      // Auto-completar con la siguiente referencia
      setReference(result.next_reference)
      setReferenceInfo({
        highest: result.highest_found,
        count: result.count
      })
    } catch (error) {
      console.error('[ProductCreator] Error obteniendo siguiente referencia:', error)
    } finally {
      setReferenceLoading(false)
    }
  }

  // WebSocket para progreso en tiempo real - connect when wsEnabled is true
  const { isConnected } = useWebSocket(
    wsEnabled ? `ws://localhost:8000/api/product-ai/ws/progress/${clientId}` : null,
    {
      onMessage: (data: ProgressMessage) => {
        if (data.type === 'progress' && data.step && data.total && data.message && data.percentage !== undefined) {
          setProgress({
            step: data.step,
            total: data.total,
            message: data.message,
            percentage: data.percentage,
            elapsed_seconds: data.elapsed_seconds,
          })
        }
      },
      onOpen: () => console.log('[ProductCreator] WebSocket conectado'),
      onClose: () => {
        console.log('[ProductCreator] WebSocket desconectado')
        setWsEnabled(false)
      },
    }
  )

  // Mutation para crear producto
  const createMutation = useMutation({
    mutationFn: async (data: ProductCreateRequest) => {
      setProgress({ step: 0, total: 7, message: 'Iniciando...', percentage: 0 })
      const response = await productAI.createProduct(data)
      return response
    },
    onSuccess: async (data) => {
      setProgress(null)
      setWsEnabled(false)

      // Check for duplicate warning
      if (data.status === 'duplicate_warning' && data.existing_product) {
        setDuplicateInfo({
          id_product: data.existing_product.id_product,
          name: data.existing_product.name,
          reference: data.existing_product.reference
        })
        setShowDuplicateModal(true)
        return  // Don't process further, wait for user decision
      }

      setResult(data)

      // Vincular producto del proveedor si venimos de un catálogo de proveedor
      if (supplierIdParam && productIdParam && (data.product_id || data.reference)) {
        try {
          console.log('[ProductCreator] Vinculando producto del proveedor...', {
            supplierIdParam,
            productIdParam,
            prestashop_id: data.product_id,
            reference: data.reference,
            factusol_created: data.factusol_created
          })

          await supplierCatalogsApi.updateProduct(supplierIdParam, productIdParam, {
            our_reference: data.reference || undefined,
            prestashop_id: data.product_id || undefined,
            factusol_reference: data.reference || undefined,
            published_prestashop: !!data.product_id,
            published_factusol: !!data.factusol_created
          })

          console.log('[ProductCreator] Producto del proveedor vinculado correctamente')
          toast.success('Producto vinculado', 'El producto ha sido vinculado al catálogo del proveedor')
        } catch (linkError) {
          console.error('[ProductCreator] Error vinculando producto del proveedor:', linkError)
          // No mostramos error al usuario porque el producto se creó correctamente
        }
      }
    },
    onError: (error: any) => {
      console.error('Error creando producto:', error)
      toast.error('Error creando producto', getErrorMessage(error))
      setProgress(null)
      setWsEnabled(false)
    },
  })

  // Mutation para añadir a cola
  const addToQueueMutation = useMutation({
    mutationFn: async (data: {
      name: string
      reference?: string
      cost_price?: number
      text_content?: string
      urls?: string[]
      image_urls?: string[]
      category_id?: number
      warranty_duration?: string
      factusol_family?: string
      factusol_supplier?: string
      factusol_supplier_ref?: string
      supplier_catalog_id?: number
      supplier_product_id?: number
      auto_approve?: boolean
      ai_model?: string
    }) => {
      return await productAI.addToQueue(data)
    },
    onSuccess: (data) => {
      toast.success('Trabajo añadido a la cola', `ID: ${data.job_id}. Redirigiendo a la cola...`)
      // Navegar a la página de cola
      navigate('/products/queue')
    },
    onError: (error: any) => {
      console.error('Error añadiendo a cola:', error)
      toast.error('Error añadiendo a cola', getErrorMessage(error))
    },
  })

  const handleAddUrl = () => {
    setUrls([...urls, ''])
  }

  const handleRemoveUrl = (index: number) => {
    setUrls(urls.filter((_, i) => i !== index))
  }

  const handleUrlChange = (index: number, value: string) => {
    const newUrls = [...urls]
    newUrls[index] = value
    setUrls(newUrls)
  }

  const handleImageChange = (index: number, file: File | null) => {
    // Clear previous error for this index
    const newErrors = { ...imageErrors }
    delete newErrors[index]

    if (file) {
      // Validate file type
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        newErrors[index] = `Formato no soportado. Use: JPG, PNG, WebP o GIF`
        setImageErrors(newErrors)
        return
      }

      // Validate file size
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        const sizeMB = (file.size / 1024 / 1024).toFixed(2)
        newErrors[index] = `Imagen demasiado grande (${sizeMB}MB). Max: ${MAX_IMAGE_SIZE_MB}MB`
        setImageErrors(newErrors)
        return
      }

      // File is valid
      const newImages = [...imageFiles]
      newImages[index] = file
      setImageFiles(newImages)
    } else {
      // File removed
      const newImages = [...imageFiles]
      newImages.splice(index, 1)
      setImageFiles(newImages)
    }

    setImageErrors(newErrors)
  }

  const handleAddImage = () => {
    // No añadimos archivo vacío, solo incrementamos la UI
    // El archivo se añade cuando el usuario selecciona uno
  }

  const handleRemoveImage = (index: number) => {
    setImageFiles(imageFiles.filter((_, i) => i !== index))
  }

  // Procesar imágenes del proveedor con IA (secuencialmente, 1 en 1)
  const handleProcessSupplierImages = async () => {
    if (!supplierIdParam || !productIdParam || selectedImageIndices.length === 0) {
      toast.warning('Selecciona al menos una imagen para procesar')
      return
    }

    // Instrucciones por defecto si el usuario no escribe nada
    const instructions = imageInstructions.trim() || 'Eliminar marcas de agua y traducir texto en inglés al español'

    setProcessingImages(true)
    setTotalImageCost({ usd: 0, eur: 0 })
    let accumulatedCost = { usd: 0, eur: 0 }

    try {
      // Procesar cada imagen secuencialmente
      for (let i = 0; i < selectedImageIndices.length; i++) {
        const imageIndex = selectedImageIndices[i]
        setCurrentProcessingIndex(i)

        const result = await supplierCatalogsApi.processProductImage(
          supplierIdParam,
          productIdParam,
          imageIndex,
          instructions
        )

        if (result.success && result.processed_image) {
          // Actualizar la imagen procesada en el estado
          setSupplierImages(prev => prev.map(img =>
            img.index === imageIndex
              ? { ...img, processed_base64: result.processed_image, has_edited_version: true }
              : img
          ))
          // Acumular coste
          accumulatedCost.usd += result.cost_usd
          accumulatedCost.eur += result.cost_eur
          setTotalImageCost({ ...accumulatedCost })

          // Esperar 5 segundos antes de la siguiente imagen (evitar rate limit)
          if (i < selectedImageIndices.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 5000))
          }
        } else {
          toast.error(`Error procesando imagen ${imageIndex + 1}`, result.error || 'Error desconocido')
        }
      }

      toast.success(`${selectedImageIndices.length} imágenes procesadas`)
    } catch (error: any) {
      console.error('Error procesando imágenes:', error)
      toast.error('Error procesando imágenes', error.message)
    } finally {
      setProcessingImages(false)
      setCurrentProcessingIndex(null)
    }
  }

  // Toggle selección de imagen del proveedor
  const toggleSupplierImageSelection = (index: number) => {
    setSelectedImageIndices(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    )
  }

  // Toggle selección de imagen ITSys
  const toggleItsysImageSelection = (url: string) => {
    setSelectedItsysImages(prev => {
      if (prev.includes(url)) {
        // Deseleccionando imagen
        const newSelection = prev.filter(u => u !== url)
        // Si era la cover, establecer la primera seleccionada como nueva cover
        if (itsysCoverImage === url) {
          setItsysCoverImage(newSelection.length > 0 ? newSelection[0] : null)
        }
        return newSelection
      } else {
        // Seleccionando imagen
        const newSelection = [...prev, url]
        // Si no hay cover, establecer esta como cover
        if (!itsysCoverImage) {
          setItsysCoverImage(url)
        }
        return newSelection
      }
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Mark all fields as touched to show errors
    touchAll()

    // Check URL format errors
    const hasUrlFormatErrors = Object.keys(urlErrors).length > 0

    // Validate form
    if (!isValid || hasUrlFormatErrors) {
      toast.warning('Por favor, corrige los errores del formulario')
      return
    }

    // Validar según el modo de entrada
    if (inputMode === 'url') {
      const validUrls = urls.filter((url) => url.trim() !== '')
      if (validUrls.length === 0) {
        toast.warning('Debe proporcionar al menos una URL')
        return
      }
    } else {
      if (!textContent.trim()) {
        toast.warning('Debe proporcionar contenido de texto')
        return
      }
    }

    const validUrls = inputMode === 'url' ? urls.filter((url) => url.trim() !== '') : []

    // Ordenar imágenes ITSys para que la cover (principal) esté primero
    const sortedItsysUrls = [...selectedItsysImages].sort((a, b) => {
      if (a === itsysCoverImage) return -1
      if (b === itsysCoverImage) return 1
      return 0
    })

    // Combinar URLs de imágenes: ITSys seleccionadas (cover primero) + otras seleccionadas
    // Usar el origen actual para las URLs ITSys (no localhost hardcoded)
    const baseUrl = window.location.origin
    const allImageUrls = [
      ...sortedItsysUrls.map(url => `${baseUrl}${url}`),
      ...selectedScrapedImages
    ].filter(url => url)

    // Convertir las imágenes del proveedor seleccionadas (base64) a File para enviarlas
    // junto a las imágenes manuales. Si fueron procesadas con IA, usa la versión procesada.
    const supplierFiles: File[] = supplierImages
      .filter(img => selectedImageIndices.includes(img.index))
      .map(img => {
        const dataUrl = img.processed_base64 || img.base64
        const [meta, b64] = dataUrl.split(',')
        const mime = meta.match(/data:([^;]+)/)?.[1] || 'image/jpeg'
        const bin = atob(b64)
        const bytes = new Uint8Array(bin.length)
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
        return new File([bytes], img.filename || `supplier_${img.index}.jpg`, { type: mime })
      })

    const requestData: ProductCreateRequest = {
      urls: validUrls,
      text_content: inputMode === 'text' ? textContent : additionalInfo,
      category_id: categoryId ? parseInt(categoryId) : undefined,
      reference: reference || undefined,
      cost_price: costPrice ? parseFloat(costPrice) : undefined,
      primary_language: 'es',
      preview_only: previewOnly,
      warranty_duration: warrantyDuration,
      images: [...imageFiles.filter(f => f !== null), ...supplierFiles],
      image_urls: allImageUrls.length > 0 ? allImageUrls : undefined,

      // Factusol Data
      factusol_create: createInFactusol,
      factusol_family: selectedFamily,
      factusol_supplier: selectedSupplier,
      factusol_supplier_ref: supplierRef,

      // WebSocket client ID for progress tracking (only when not preview)
      client_id: !previewOnly ? clientId : undefined,

      // OpenAI model selection
      ai_model: aiModel,
    }

    // Guardar los datos de la petición para poder reutilizarlos
    setLastRequestData(requestData)

    // Si se quiere añadir a cola
    if (!previewOnly && addToQueue) {
      // Crear nombre descriptivo para el trabajo
      const jobName = reference
        ? `${reference} - ${textContent.substring(0, 50) || validUrls[0]?.substring(0, 50) || 'Producto'}`
        : textContent.substring(0, 80) || validUrls[0]?.substring(0, 80) || 'Producto sin nombre'

      const queueData = {
        name: jobName,
        reference: reference || undefined,
        cost_price: costPrice ? parseFloat(costPrice) : undefined,
        text_content: inputMode === 'text' ? textContent : additionalInfo,
        urls: validUrls.length > 0 ? validUrls : undefined,
        image_urls: selectedScrapedImages.length > 0 ? selectedScrapedImages : undefined,
        category_id: categoryId ? parseInt(categoryId) : undefined,
        warranty_duration: warrantyDuration || '3 años',
        factusol_family: selectedFamily || undefined,
        factusol_supplier: selectedSupplier ? String(selectedSupplier) : undefined,
        factusol_supplier_ref: supplierRef || undefined,
        supplier_catalog_id: supplierIdParam || undefined,
        supplier_product_id: productIdParam || undefined,
        auto_approve: autoApprove ?? true,
        ai_model: aiModel || 'gemini-2.5-pro',
      }

      // Log para debug - identificar campos problemáticos
      console.log('[ProductCreator] Queue data:', JSON.stringify(queueData, null, 2))

      addToQueueMutation.mutate(queueData)
      return
    }

    // Si NO es preview, mostrar modal de confirmación
    if (!previewOnly) {
      setPendingRequestData(requestData)
      setShowConfirmModal(true)
      return
    }

    // Si es preview, ejecutar directamente
    createMutation.mutate(requestData)
  }

  // Función para confirmar y ejecutar la creación
  const handleConfirmCreate = () => {
    if (!pendingRequestData) return

    // Enable WebSocket for real-time progress
    setWsEnabled(true)
    setShowConfirmModal(false)

    // Small delay to allow WebSocket to connect before submitting
    setTimeout(() => {
      createMutation.mutate(pendingRequestData)
      setPendingRequestData(null)
    }, 300)
  }

  // Función para cancelar la creación
  const handleCancelCreate = () => {
    setShowConfirmModal(false)
    setPendingRequestData(null)
  }

  const handleClear = () => {
    setUrls([''])
    setTextContent('')
    setAdditionalInfo('')
    setCategoryId('')
    setReference('')
    setReferenceInfo(null)
    setSupplierReference('')
    setCostPrice('')
    setImageFiles([])
    setImageErrors({})
    setUrlErrors({})
    setSelectedScrapedImages([])
    setItsysImages([])
    setSelectedItsysImages([])
    setItsysCoverImage(null)
    setItsysCategory(null)
    setResult(null)
    setProgress(null)
    resetTouched()
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
          🤖 Crear Producto con IA
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          Proporciona información del producto y la IA creará la ficha completa traducida a 5 idiomas
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Formulario */}
        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Selector de modo de entrada */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                📝 Fuente de información
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setInputMode('url')}
                  className={`px-4 py-3 rounded-lg border-2 transition-all ${inputMode === 'url'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-semibold'
                    : 'border-slate-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-gray-500'
                    }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-1">🔗</div>
                    <div className="text-sm">Desde URL</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setInputMode('text')}
                  className={`px-4 py-3 rounded-lg border-2 transition-all ${inputMode === 'text'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-semibold'
                    : 'border-slate-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-gray-500'
                    }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-1">📋</div>
                    <div className="text-sm">Texto Manual</div>
                  </div>
                </button>
              </div>
            </div>

            {/* URLs - Solo visible cuando inputMode === 'url' */}
            {inputMode === 'url' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  🔗 URLs con información del producto
                </label>
                <div className="space-y-2">
                  {urls.map((url, index) => (
                    <div key={index} className="space-y-1">
                      <div className="flex gap-2">
                        <input
                          type="url"
                          value={url}
                          onChange={(e) => {
                            handleUrlChange(index, e.target.value)
                            validateUrl(index, e.target.value)
                          }}
                          onBlur={() => validateUrl(index, url)}
                          placeholder="https://example.com/producto"
                          className={`input flex-1 ${urlErrors[index] ? 'border-red-500 dark:border-red-400 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                          aria-invalid={!!urlErrors[index]}
                        />
                        {urls.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveUrl(index)}
                            className="btn btn-danger"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      {urlErrors[index] && (
                        <p className="text-sm text-danger-600 dark:text-danger-400" role="alert">
                          {urlErrors[index]}
                        </p>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddUrl}
                    className="btn btn-secondary w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar URL
                  </button>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                  💡 Con OpenAI, la IA accede directamente a la URL sin necesidad de scraping
                </p>

                {/* Información adicional - complementa la URL */}
                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    📝 Información adicional (opcional)
                  </label>
                  <textarea
                    value={additionalInfo}
                    onChange={(e) => setAdditionalInfo(e.target.value)}
                    placeholder="Añade información extra que no esté en la URL: especificaciones técnicas, notas, instrucciones para la IA (ej: 'enfatizar que es producto premium', 'incluir compatibilidad con X')..."
                    rows={4}
                    className="input"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Esta información se enviará junto con el contenido de la URL para crear la ficha
                  </p>
                </div>
              </div>
            )}

            {/* Texto libre - Solo visible cuando inputMode === 'text' */}
            {inputMode === 'text' && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${visibleErrors.textContent?.hasError ? 'text-danger-600 dark:text-danger-400' : 'text-slate-700 dark:text-slate-300'}`}>
                  📋 Información del producto <span className="text-danger-500">*</span>
                </label>
                <textarea
                  value={textContent}
                  onChange={(e) => setTextContent(e.target.value)}
                  onBlur={() => getFieldProps('textContent').onBlur()}
                  placeholder="Incluye toda la información disponible del producto: nombre, características técnicas, especificaciones, compatibilidad, part number, etc."
                  rows={10}
                  className={`input ${visibleErrors.textContent?.hasError ? 'border-red-500 dark:border-red-400 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                  aria-invalid={visibleErrors.textContent?.hasError}
                />
                {visibleErrors.textContent?.hasError ? (
                  <p className="text-sm text-danger-600 dark:text-danger-400 mt-1" role="alert">
                    {visibleErrors.textContent.message}
                  </p>
                ) : (
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                    💡 Cuanta más información proporciones, mejor será la ficha generada
                  </p>
                )}
              </div>
            )}

            {/* Imágenes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <Image className="w-4 h-4 inline mr-2" />
                Imágenes del producto
              </label>
              <div className="space-y-2">
                {imageFiles.map((file, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex gap-2 items-center">
                      <div className="flex-1 relative">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null
                            handleImageChange(index, file)
                          }}
                          className="hidden"
                          id={`image-input-${index}`}
                        />
                        <label
                          htmlFor={`image-input-${index}`}
                          className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 ${
                            imageErrors[index]
                              ? 'border-red-500 dark:border-red-400'
                              : 'border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
                          }`}
                        >
                          <Upload className="w-4 h-4" />
                          <span className="text-sm truncate">
                            {file ? file.name : 'Seleccionar imagen...'}
                          </span>
                          {file && (
                            <span className="text-xs text-slate-400 ml-auto">
                              {(file.size / 1024 / 1024).toFixed(2)}MB
                            </span>
                          )}
                        </label>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="btn btn-danger"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {imageErrors[index] && (
                      <p className="text-xs text-danger-500 dark:text-danger-400 pl-1">
                        {imageErrors[index]}
                      </p>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setImageFiles([...imageFiles, null as any])}
                  className="btn btn-secondary w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar imagen
                </button>
              </div>
              {imageFiles.length > 0 && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                  {imageFiles.filter(f => f).length} imagen(es) seleccionada(s)
                </p>
              )}
            </div>

            {/* Imágenes del proveedor (precargadas desde el catálogo, p.ej. GreenCell) */}
            {(loadingSupplierImages || supplierImages.length > 0) && (
              <div className="border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 bg-emerald-50/50 dark:bg-emerald-900/20">
                <label className="block text-sm font-medium text-emerald-800 dark:text-emerald-300 mb-3">
                  <Image className="w-4 h-4 inline mr-2" />
                  Imágenes del proveedor
                </label>

                {loadingSupplierImages ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
                    <span className="ml-2 text-sm text-slate-600 dark:text-slate-400">Cargando imágenes del proveedor...</span>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      {supplierImages.map((img) => {
                        const isSelected = selectedImageIndices.includes(img.index)
                        const isProcessing = processingImages && currentProcessingIndex !== null && selectedImageIndices[currentProcessingIndex] === img.index
                        const displaySrc = img.processed_base64 || img.base64
                        return (
                          <div
                            key={img.index}
                            onClick={() => toggleSupplierImageSelection(img.index)}
                            className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                              isSelected
                                ? 'border-emerald-500 ring-2 ring-emerald-300'
                                : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300'
                            }`}
                          >
                            <img
                              src={displaySrc}
                              alt={img.filename}
                              className="w-full h-24 object-cover bg-white"
                            />
                            <div className={`absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                              isSelected
                                ? 'bg-emerald-500 text-white'
                                : 'bg-white/80 border border-slate-300'
                            }`}>
                              {isSelected && <Check className="w-3 h-3" />}
                            </div>
                            {img.processed_base64 && (
                              <div className="absolute bottom-1 left-1 bg-emerald-600 text-white text-xs px-1.5 py-0.5 rounded">
                                Procesada
                              </div>
                            )}
                            {isProcessing && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                <Loader2 className="w-6 h-6 animate-spin text-white" />
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>

                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                      {selectedImageIndices.length} de {supplierImages.length} imagen(es) seleccionada(s).
                      Las seleccionadas se enviarán al crear el producto.
                    </p>

                    {/* Procesado opcional con IA (eliminar marcas de agua, traducir, etc.) */}
                    <div className="border-t border-emerald-200 dark:border-emerald-800 pt-3 mt-3">
                      <label className="block text-xs font-medium text-emerald-800 dark:text-emerald-300 mb-2">
                        Procesar con IA (opcional)
                      </label>
                      <textarea
                        value={imageInstructions}
                        onChange={(e) => setImageInstructions(e.target.value)}
                        placeholder="Instrucciones (opcional). Ej: Eliminar marcas de agua y traducir texto en inglés al español"
                        rows={2}
                        disabled={processingImages}
                        className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 disabled:opacity-50"
                      />
                      <div className="flex items-center justify-between mt-2">
                        <button
                          type="button"
                          onClick={handleProcessSupplierImages}
                          disabled={processingImages || selectedImageIndices.length === 0}
                          className="btn btn-secondary text-sm"
                        >
                          {processingImages ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Procesando {currentProcessingIndex !== null ? `${currentProcessingIndex + 1}/${selectedImageIndices.length}` : ''}
                            </>
                          ) : (
                            <>
                              <Image className="w-4 h-4 mr-2" />
                              Procesar imágenes con IA
                            </>
                          )}
                        </button>
                        {totalImageCost.eur > 0 && (
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            Coste: {totalImageCost.eur.toFixed(4)} € (${totalImageCost.usd.toFixed(4)})
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ITSYS sin fotos limpias: limpiar la marca de agua con PixelBin */}
            {isItsysSupplier && supplierReference.length >= 3 && !loadingItsysImages && (cleaningItsys || itsysImages.length === 0 || itsysCleanMsg) && (
              <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50/50 dark:bg-blue-900/20">
                {cleaningItsys ? (
                  <div className="flex items-center text-sm text-slate-600 dark:text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin text-primary-600 mr-2" />
                    Descargando las fotos de ITSYS y quitando la marca de agua con PixelBin (~6 s por foto)...
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <p className={`text-sm ${
                      itsysCleanMsg?.type === 'ok'
                        ? 'text-success-700 dark:text-success-400'
                        : itsysCleanMsg
                          ? 'text-danger-700 dark:text-danger-400'
                          : 'text-slate-600 dark:text-slate-400'
                    }`}>
                      {itsysCleanMsg?.text || `No hay fotos sin marca de agua de ${supplierReference}.`}
                    </p>
                    {itsysImages.length === 0 && (
                      <button
                        type="button"
                        onClick={() => cleanItsysImages(supplierReference)}
                        className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700"
                        title="Descarga las fotos de ITSYS y quita la marca de agua con PixelBin (1 crédito por foto)"
                      >
                        <Sparkles className="w-4 h-4" />
                        {itsysCleanMsg ? 'Reintentar' : 'Limpiar fotos de ITSYS'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Imágenes ITSys (auto-detectadas por referencia del proveedor) */}
            {(itsysImages.length > 0 || loadingItsysImages) && (
              <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50/50 dark:bg-blue-900/20">
                <label className="block text-sm font-medium text-blue-800 dark:text-blue-300 mb-3">
                  <Image className="w-4 h-4 inline mr-2" />
                  Imágenes ITSys detectadas
                  {itsysCategory && (
                    <span className="ml-2 text-xs font-normal text-primary-600 dark:text-primary-400">
                      ({itsysCategory})
                    </span>
                  )}
                </label>

                {loadingItsysImages ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                    <span className="ml-2 text-sm text-slate-600 dark:text-slate-400">Buscando imágenes...</span>
                  </div>
                ) : (
                  <>
                    {/* Grid de imágenes */}
                    <div className="grid grid-cols-3 gap-3 mb-3 relative">
                      {itsysImages.map((img) => {
                        const isSelected = selectedItsysImages.includes(img.url)
                        const isCover = itsysCoverImage === img.url
                        return (
                          <div
                            key={img.path}
                            onClick={() => toggleItsysImageSelection(img.url)}
                            onMouseEnter={() => setHoveredItsysImage(img)}
                            onMouseLeave={() => setHoveredItsysImage(null)}
                            className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                              isCover
                                ? 'border-yellow-500 ring-2 ring-yellow-300'
                                : isSelected
                                  ? 'border-blue-500 ring-2 ring-blue-300'
                                  : 'border-slate-200 dark:border-slate-700 hover:border-blue-300'
                            }`}
                          >
                            <img
                              src={img.url}
                              alt={img.filename}
                              className="w-full h-24 object-cover bg-white"
                              onError={(e) => {
                                console.error('[ITSys] Error loading image:', img.url)
                                ;(e.target as HTMLImageElement).style.display = 'none'
                              }}
                            />
                            {/* Cover star indicator - clickable to set as cover */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                if (isSelected) {
                                  setItsysCoverImage(img.url)
                                }
                              }}
                              title={isCover ? 'Imagen principal' : 'Establecer como principal'}
                              className={`absolute top-1 left-1 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                                isCover
                                  ? 'bg-yellow-500 text-white'
                                  : isSelected
                                    ? 'bg-white/80 text-slate-400 hover:text-yellow-500 hover:bg-yellow-50'
                                    : 'bg-white/50 text-slate-300'
                              }`}
                            >
                              <Star className={`w-4 h-4 ${isCover ? 'fill-current' : ''}`} />
                            </button>
                            {/* Checkbox indicator */}
                            <div className={`absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                              isSelected
                                ? 'bg-blue-500 text-white'
                                : 'bg-white/80 border border-slate-300'
                            }`}>
                              {isSelected && (
                                <Check className="w-3 h-3" />
                              )}
                            </div>
                            {/* Badge de resolución */}
                            <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                              {img.width}x{img.height}
                            </div>
                          </div>
                        )
                      })}

                    </div>

                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {selectedItsysImages.length} de {itsysImages.length} imagen(es) seleccionada(s).
                      <Star className="w-3 h-3 inline mx-1 text-yellow-500" /> = imagen principal en PrestaShop.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Campos opcionales */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <SearchableSelect
                  label="Categoría PrestaShop"
                  options={prestashopCategories.map((cat) => ({
                    value: cat.id.toString(),
                    label: cat.display_name,
                  }))}
                  value={categoryId}
                  onChange={setCategoryId}
                  placeholder="Buscar categoría..."
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${visibleErrors.reference?.hasError ? 'text-danger-600 dark:text-danger-400' : 'text-slate-700 dark:text-slate-300'}`}>
                  Referencia
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={reference}
                    onChange={(e) => {
                      setReference(e.target.value.toUpperCase())
                      setReferenceInfo(null) // Limpiar info al escribir
                    }}
                    onBlur={() => getFieldProps('reference').onBlur()}
                    placeholder="Prefijo para auto-numerar"
                    maxLength={64}
                    className={`input flex-1 ${visibleErrors.reference?.hasError ? 'border-red-500 dark:border-red-400 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                    aria-invalid={visibleErrors.reference?.hasError}
                  />
                  <button
                    type="button"
                    onClick={fetchNextSequentialReference}
                    disabled={!reference || reference.length < 1 || referenceLoading}
                    title="Buscar siguiente número correlativo en Factusol"
                    className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                  >
                    {referenceLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>#</>
                    )}
                  </button>
                </div>
                {visibleErrors.reference?.hasError && (
                  <p className="text-sm text-danger-600 dark:text-danger-400 mt-1" role="alert">
                    {visibleErrors.reference.message}
                  </p>
                )}
                {referenceInfo && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {referenceInfo.count > 0 ? (
                      <>Última: <span className="font-mono">{referenceInfo.highest}</span> ({referenceInfo.count} productos)</>
                    ) : (
                      <>Primera referencia con este prefijo</>
                    )}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">
                  Referencia Proveedor
                </label>
                <input
                  type="text"
                  value={supplierReference}
                  onChange={(e) => setSupplierReference(e.target.value)}
                  placeholder="Opcional"
                  maxLength={64}
                  className="input"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${visibleErrors.costPrice?.hasError ? 'text-danger-600 dark:text-danger-400' : 'text-slate-700 dark:text-slate-300'}`}>
                  Precio de Coste (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={costPrice}
                  onChange={(e) => setCostPrice(e.target.value)}
                  onBlur={() => getFieldProps('costPrice').onBlur()}
                  placeholder="0.00"
                  className={`input ${visibleErrors.costPrice?.hasError ? 'border-red-500 dark:border-red-400 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                  aria-invalid={visibleErrors.costPrice?.hasError}
                />
                {visibleErrors.costPrice?.hasError && (
                  <p className="text-sm text-danger-600 dark:text-danger-400 mt-1" role="alert">
                    {visibleErrors.costPrice.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Duración de garantía
                </label>
                <select
                  value={warrantyDuration}
                  onChange={(e) => setWarrantyDuration(e.target.value)}
                  className="input"
                >
                  <option value="6 meses">6 meses</option>
                  <option value="12 meses">12 meses</option>
                  <option value="1 año">1 año</option>
                  <option value="2 años">2 años</option>
                  <option value="3 años">3 años</option>
                </select>
              </div>
            </div>

            {/* Factusol Integration */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="createInFactusol"
                  checked={createInFactusol}
                  onChange={(e) => setCreateInFactusol(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="createInFactusol" className="ml-2 text-sm font-medium text-slate-900 dark:text-white">
                  Crear también en Factusol
                </label>
              </div>

              {createInFactusol && (
                <div className="space-y-4 pl-6 border-l-2 border-primary-100 dark:border-primary-800 ml-2">
                  <div>
                    <SearchableSelect
                      label="Familia Factusol"
                      options={factusolFamilies.map((fam: any) => ({
                        value: fam.CODFAM,
                        label: fam.DESFAM,
                      }))}
                      value={selectedFamily}
                      onChange={(value) => {
                        setSelectedFamily(value)
                        getFieldProps('factusolFamily').onBlur()
                      }}
                      placeholder="Buscar familia..."
                      required={createInFactusol}
                    />
                    {visibleErrors.factusolFamily?.hasError && (
                      <p className="text-sm text-danger-600 dark:text-danger-400 mt-1" role="alert">
                        {visibleErrors.factusolFamily.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <SearchableSelect
                      label="Proveedor"
                      options={factusolSuppliers.map((sup: any) => ({
                        value: sup.CODPRO,
                        label: sup.NOFPRO,
                      }))}
                      value={selectedSupplier}
                      onChange={(value) => {
                        setSelectedSupplier(value)
                        getFieldProps('factusolSupplier').onBlur()
                      }}
                      placeholder="Buscar proveedor..."
                      required={createInFactusol}
                    />
                    {visibleErrors.factusolSupplier?.hasError && (
                      <p className="text-sm text-danger-600 dark:text-danger-400 mt-1" role="alert">
                        {visibleErrors.factusolSupplier.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Referencia Proveedor
                    </label>
                    <input
                      type="text"
                      value={supplierRef}
                      onChange={(e) => setSupplierRef(e.target.value)}
                      className="input w-full"
                      placeholder="Ref. del proveedor"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Preview Only */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="previewOnly"
                checked={previewOnly}
                onChange={(e) => {
                  setPreviewOnly(e.target.checked)
                  if (e.target.checked) {
                    setAddToQueue(false)
                    setAutoApprove(false)
                  }
                }}
                className="w-4 h-4 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="previewOnly" className="ml-2 text-sm text-slate-700 dark:text-slate-300">
                Solo vista previa (no crear en PrestaShop)
              </label>
            </div>

            {/* Add to Queue */}
            {!previewOnly && (
              <div className="space-y-3 pl-6 border-l-2 border-primary-200 dark:border-primary-800">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="addToQueue"
                    checked={addToQueue}
                    onChange={(e) => setAddToQueue(e.target.checked)}
                    className="w-4 h-4 text-purple-600 border-slate-300 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="addToQueue" className="ml-2 text-sm text-slate-700 dark:text-slate-300">
                    Añadir a cola de trabajo (procesar en segundo plano)
                  </label>
                </div>

                {/* Auto Approve - only visible when addToQueue is checked */}
                {addToQueue && (
                  <div className="flex items-center ml-6">
                    <input
                      type="checkbox"
                      id="autoApprove"
                      checked={autoApprove}
                      onChange={(e) => setAutoApprove(e.target.checked)}
                      className="w-4 h-4 text-success-600 border-slate-300 rounded focus:ring-green-500"
                    />
                    <label htmlFor="autoApprove" className="ml-2 text-sm text-slate-700 dark:text-slate-300">
                      Aprobar y publicar automaticamente
                    </label>
                  </div>
                )}

                {addToQueue && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 ml-6">
                    {autoApprove
                      ? 'El producto se creara y publicara automaticamente sin revision manual'
                      : 'El producto se procesara en segundo plano. Podras revisarlo antes de publicar'}
                  </p>
                )}
              </div>
            )}

            {/* Botones */}
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={createMutation.isPending || addToQueueMutation.isPending || loadingSupplierImages}
                className={`btn flex-1 ${addToQueue ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'btn-primary'}`}
              >
                {createMutation.isPending || addToQueueMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : loadingSupplierImages ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Cargando imágenes del proveedor…
                  </>
                ) : previewOnly ? (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    Vista Previa
                  </>
                ) : addToQueue ? (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Añadir a Cola
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Crear Producto
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleClear}
                className="btn btn-secondary"
              >
                Limpiar
              </button>
            </div>
          </form>
        </div>

        {/* Progreso y Resultado */}
        <div className="space-y-6">
          {/* Progreso */}
          {progress && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  Creando Producto...
                </h3>
                <div className="flex items-center gap-2 text-sm">
                  {isConnected && (
                    <span className="flex items-center gap-1 text-success-600 dark:text-success-400">
                      <span className="w-2 h-2 rounded-full bg-success-500 animate-pulse"></span>
                      En vivo
                    </span>
                  )}
                  {progress.elapsed_seconds !== undefined && (
                    <span className="text-slate-500 dark:text-slate-400">
                      {progress.elapsed_seconds.toFixed(1)}s
                    </span>
                  )}
                </div>
              </div>

              {/* Step indicators */}
              <div className="space-y-2 mb-4">
                {PROGRESS_STEPS.map((step) => (
                  <div key={step.step} className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                      progress.step > step.step
                        ? 'bg-success-500 text-white'
                        : progress.step === step.step
                          ? 'bg-primary-500 text-white animate-pulse'
                          : 'bg-slate-200 dark:bg-slate-700 text-slate-500'
                    }`}>
                      {progress.step > step.step ? (
                        <Check className="w-3 h-3" />
                      ) : (
                        step.step
                      )}
                    </div>
                    <span className={`text-sm ${
                      progress.step >= step.step
                        ? 'text-slate-900 dark:text-white font-medium'
                        : 'text-slate-400 dark:text-slate-500'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                ))}
              </div>

              {/* Progress bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400">
                    {progress.message}
                  </span>
                  <span className="font-semibold text-primary-600 dark:text-primary-400">
                    {progress.percentage}%
                  </span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Resultado */}
          {result && (
            <>
              {/* Información del resultado */}
              <div className="card">
                <h3 className="text-lg font-semibold mb-4">
                  {result.status === 'success' ? '✅ Éxito' : '📊 Vista Previa'}
                </h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="font-medium text-slate-700 dark:text-slate-300">Estado:</span>{' '}
                    <span className="text-success-600 dark:text-success-400">{result.status}</span>
                  </div>
                  {result.product_id && (
                    <div className="text-gray-900 dark:text-gray-100">
                      <span className="font-medium text-slate-700 dark:text-slate-300">ID Producto:</span>{' '}
                      <span className="font-semibold text-primary-600 dark:text-primary-400">{result.product_id}</span>
                      {result.status === 'success' && (
                        <a
                          href={`https://www.jvs-informatica.com/index.php?id_product=${result.product_id}&controller=product&id_lang=1`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-3 inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          Ver ficha en la tienda (ES)
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  )}
                  {result.reference && (
                    <div className="text-gray-900 dark:text-gray-100">
                      <span className="font-medium text-slate-700 dark:text-slate-300">Referencia:</span>{' '}
                      <span className="font-semibold">{result.reference}</span>
                    </div>
                  )}
                  <div className="text-gray-900 dark:text-gray-100">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Tiempo:</span>{' '}
                    {result.execution_time?.toFixed(2) || '0.00'}s
                  </div>
                  {result.factusol_created !== undefined && (
                    <div className="text-gray-900 dark:text-gray-100">
                      <span className="font-medium text-slate-700 dark:text-slate-300">Factusol:</span>{' '}
                      <span className={result.factusol_created ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}>
                        {result.factusol_created ? '✅ Creado' : '❌ No creado'}
                      </span>
                    </div>
                  )}
                  {/* OpenAI Cost Display */}
                  {result.openai_usage && (
                    <OpenAICostDisplay
                      usage={result.openai_usage}
                      showDetails={true}
                      className="mt-3"
                    />
                  )}
                  {result.warnings && result.warnings.length > 0 && (
                    <div>
                      <span className="font-medium">Advertencias:</span>
                      <ul className="list-disc list-inside mt-1 text-yellow-600 dark:text-yellow-400">
                        {result.warnings.map((warning: string, i: number) => (
                          <li key={i}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Capa AI-ready (GEO + SEO): score y checks */}
                  {(result.ai_readiness || result.preview?._ai_readiness) && (
                    <AiReadinessBadge readiness={result.ai_readiness || result.preview?._ai_readiness} className="mt-3" />
                  )}
                  {/* Respuesta directa y FAQ generadas (solo lectura) */}
                  {(result.preview?.direct_answer || (result.preview?.faqs && result.preview.faqs.length > 0)) && (
                    <div className="mt-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 text-sm">
                      {result.preview?.direct_answer && (
                        <div className="mb-2">
                          <span className="font-medium text-slate-700 dark:text-slate-300">Respuesta directa (para IA):</span>
                          <p className="mt-1 text-slate-800 dark:text-slate-200">{result.preview.direct_answer}</p>
                        </div>
                      )}
                      {result.preview?.faqs && result.preview.faqs.length > 0 && (
                        <div>
                          <span className="font-medium text-slate-700 dark:text-slate-300">Preguntas frecuentes ({result.preview.faqs.length}):</span>
                          <ul className="mt-1 space-y-1">
                            {result.preview.faqs.map((f: { question: string; answer: string }, i: number) => (
                              <li key={i}>
                                <span className="font-medium text-slate-800 dark:text-slate-200">{f.question}</span>
                                <p className="text-slate-600 dark:text-slate-400">{f.answer}</p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Imágenes detectadas del scraping */}
                {result.status === 'preview' && result.preview?.detected_images?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <h4 className="text-sm font-medium text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      Imágenes detectadas ({result.preview.detected_images.length})
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                      Selecciona las imágenes que quieres usar para el producto
                    </p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                      {result.preview.detected_images.slice(0, 12).map((imgUrl: string, i: number) => {
                        const isSelected = selectedScrapedImages.includes(imgUrl)
                        return (
                          <div
                            key={i}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedScrapedImages(selectedScrapedImages.filter(url => url !== imgUrl))
                              } else {
                                setSelectedScrapedImages([...selectedScrapedImages, imgUrl])
                              }
                            }}
                            className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                              isSelected
                                ? 'border-primary-500 ring-2 ring-primary-500/30'
                                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-gray-600'
                            }`}
                          >
                            <img
                              src={imgUrl}
                              alt={`Imagen ${i + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none'
                              }}
                            />
                            {isSelected && (
                              <div className="absolute inset-0 bg-primary-500/20 flex items-center justify-center">
                                <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                                  <Check className="w-4 h-4 text-white" />
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    {selectedScrapedImages.length > 0 && (
                      <p className="text-xs text-primary-600 dark:text-primary-400 mt-2">
                        {selectedScrapedImages.length} imagen(es) seleccionada(s)
                      </p>
                    )}
                  </div>
                )}

                {/* Botón para crear definitivamente después de preview */}
                {result.status === 'preview' && (
                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                      ¿Deseas crear este producto en PrestaShop?
                    </p>
                    <button
                      onClick={() => {
                        setPreviewOnly(false)
                        // Mantener los mismos datos pero crear definitivamente
                        const validUrls = inputMode === 'url' ? urls.filter((url) => url.trim() !== '') : []

                        // Ordenar imágenes ITSys para que la cover esté primero
                        const sortedItsysUrls = [...selectedItsysImages].sort((a, b) => {
                          if (a === itsysCoverImage) return -1
                          if (b === itsysCoverImage) return 1
                          return 0
                        })

                        // Combinar URLs de imágenes: ITSys + scraped
                        const baseUrl = window.location.origin
                        const allImageUrls = [
                          ...sortedItsysUrls.map(url => `${baseUrl}${url}`),
                          ...selectedScrapedImages
                        ].filter(url => url)

                        // Convertir las imágenes del proveedor seleccionadas (base64) a File
                        // — el bug previo era no incluirlas en este segundo POST post-preview
                        const supplierFilesConfirm: File[] = supplierImages
                          .filter(img => selectedImageIndices.includes(img.index))
                          .map(img => {
                            const dataUrl = img.processed_base64 || img.base64
                            const [meta, b64] = dataUrl.split(',')
                            const mime = meta.match(/data:([^;]+)/)?.[1] || 'image/jpeg'
                            const bin = atob(b64)
                            const bytes = new Uint8Array(bin.length)
                            for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
                            return new File([bytes], img.filename || `supplier_${img.index}.jpg`, { type: mime })
                          })

                        const requestData: ProductCreateRequest = {
                          urls: validUrls,
                          text_content: inputMode === 'text' ? textContent : '',
                          category_id: categoryId ? parseInt(categoryId) : undefined,
                          reference: reference || undefined,
                          cost_price: costPrice ? parseFloat(costPrice) : undefined,
                          primary_language: 'es',
                          preview_only: false, // CREAR DEFINITIVAMENTE
                          warranty_duration: warrantyDuration,
                          images: [...imageFiles.filter(f => f !== null), ...supplierFilesConfirm],
                          image_urls: allImageUrls.length > 0 ? allImageUrls : undefined,
                          // Factusol Data
                          factusol_create: createInFactusol,
                          factusol_family: selectedFamily,
                          factusol_supplier: selectedSupplier,
                          factusol_supplier_ref: supplierRef,
                          client_id: clientId,
                          // OpenAI model selection
                          ai_model: aiModel,
                        }
                        // Mostrar modal de confirmación
                        setPendingRequestData(requestData)
                        setShowConfirmModal(true)
                      }}
                      disabled={createMutation.isPending}
                      className="btn btn-primary w-full"
                    >
                      {createMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creando en PrestaShop...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Crear Producto Definitivamente
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* Vista previa renderizada del producto */}
              {(result.preview || result.extracted_data) && (
                <ProductPreview
                  data={result}
                  localImages={imageFiles.filter(f => f !== null)}
                />
              )}

              {/* Datos extraídos (JSON) */}
              {result.preview && (
                <div className="card">
                  <details className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    <summary className="font-medium cursor-pointer text-sm">
                      Ver datos JSON extraídos
                    </summary>
                    <pre className="mt-2 text-xs overflow-auto">
                      {JSON.stringify(result.preview, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal de confirmación */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={handleCancelCreate}
        onConfirm={handleConfirmCreate}
        productData={{
          title: result?.preview?.title || pendingRequestData?.text_content?.slice(0, 50),
          reference: pendingRequestData?.reference || reference,
          costPrice: pendingRequestData?.cost_price?.toString() || costPrice,
          categoryId: pendingRequestData?.category_id?.toString() || categoryId,
          categoryName: prestashopCategories.find(c => c.id.toString() === (pendingRequestData?.category_id?.toString() || categoryId))?.display_name,
          createInFactusol: pendingRequestData?.factusol_create || createInFactusol,
          factusolFamily: factusolFamilies.find(f => f.CODFAM === (pendingRequestData?.factusol_family || selectedFamily))?.DESFAM,
          factusolSupplier: factusolSuppliers.find(s => s.CODPRO === (pendingRequestData?.factusol_supplier || selectedSupplier))?.NOFPRO,
          imageCount: (pendingRequestData?.images?.length || imageFiles.filter(f => f !== null).length) + selectedItsysImages.length + selectedScrapedImages.length,
        }}
        isLoading={createMutation.isPending}
      />

      {/* Modal de advertencia de producto duplicado */}
      {showDuplicateModal && duplicateInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Producto Duplicado
              </h3>
            </div>

            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Ya existe un producto con la referencia <span className="font-semibold text-slate-900 dark:text-white">{duplicateInfo.reference}</span>:
            </p>

            <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 mb-4">
              <div className="text-sm">
                <span className="text-slate-500 dark:text-slate-400">ID:</span>{' '}
                <span className="font-mono text-slate-900 dark:text-white">{duplicateInfo.id_product}</span>
              </div>
              <div className="text-sm mt-1">
                <span className="text-slate-500 dark:text-slate-400">Nombre:</span>{' '}
                <span className="text-slate-900 dark:text-white">{duplicateInfo.name || '(sin nombre)'}</span>
              </div>
            </div>

            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              ¿Deseas crear otra ficha de producto con la misma referencia?
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDuplicateModal(false)
                  setDuplicateInfo(null)
                }}
                className="flex-1 btn btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowDuplicateModal(false)
                  setDuplicateInfo(null)
                  // Retry with force_create=true
                  if (lastRequestData) {
                    createMutation.mutate({ ...lastRequestData, force_create: true })
                  }
                }}
                disabled={createMutation.isPending}
                className="flex-1 btn btn-primary"
              >
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creando...
                  </>
                ) : (
                  'Sí, crear otra'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview ampliado de imágenes ITSys en hover */}
      {hoveredItsysImage && (
        <div
          className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-3 border border-slate-300 dark:border-slate-600">
            <img
              src={hoveredItsysImage.url}
              alt="Preview"
              className="max-w-lg max-h-[70vh] object-contain rounded-lg"
            />
            <div className="text-center mt-2 text-sm text-slate-600 dark:text-slate-400 font-medium">
              {hoveredItsysImage.width}x{hoveredItsysImage.height}
              {hoveredItsysImage.is_main && <span className="ml-2 text-primary-500">(Principal)</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
